import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { Field } from "@/pages/admin/Field";
import { FileOrUrlField } from "@/components/common/FileOrUrlField";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Copy, Archive, Trash2, ArrowUp, ArrowDown, Search, UserPlus, X, FileDown } from "lucide-react";

const ROLES = [
  ["President", "પ્રમુખ"], ["Vice President", "ઉપપ્રમુખ"], ["Secretary", "મહામંત્રી"],
  ["Joint Secretary", "સંયુક્ત મંત્રી"], ["Treasurer", "ખજાનચી"], ["Joint Treasurer", "સહ ખજાનચી"],
  ["Joint Union Secretary", "સંયુક્ત યુનિયન મંત્રી"], ["Executive Member", "કાર્યકારી સભ્ય"], ["Advisor", "સલાહકાર"],
];

export const CommitteesSection = () => {
  const [committees, setCommittees] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState(null);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [showAdd, setShowAdd] = useState(false);

  const load = () => adminApi.listCommittees().then(setCommittees);
  useEffect(() => { load(); }, []);
  const open = (c) => { setSel(c.id); setForm(JSON.parse(JSON.stringify(c))); setShowAdd(false); };
  const setMeta = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const newCommittee = async () => {
    const c = await adminApi.createCommittee({ name_en: "New Committee", name_gu: "", status: "current", members: [] });
    await load(); open(c); toast.success("Committee created");
  };
  const save = async () => {
    try { await adminApi.updateCommittee(sel, form); toast.success("Committee saved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const duplicate = async (id) => { await adminApi.duplicateCommittee(id); load(); toast.success("Duplicated to Previous committees"); };
  const archive = async (id) => { await adminApi.archiveCommittee(id); load(); toast.success("Archived"); };
  const del = async (id) => { if (!window.confirm("Delete this committee?")) return; await adminApi.deleteCommittee(id); setSel(null); load(); toast.success("Deleted"); };

  const exportPdf = async (c) => {
    const t = toast.loading("Building PDF…");
    try {
      const blob = await adminApi.committeePdf(c.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `GMOAA-${(c.name_en || "committee").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("PDF downloaded", { id: t });
    } catch { toast.error("Could not build PDF", { id: t }); }
  };

  const doSearch = async () => { const r = await adminApi.members({ q: search }); setResults(r.slice(0, 20)); };
  const addMember = (m) => {
    const exists = form.members.some((x) => x.member_id === m.id);
    if (exists) { toast.error("Already in this committee"); return; }
    setForm((f) => ({ ...f, members: [...f.members, {
      member_id: m.id, external: false, name_en: m.name_en, name_gu: m.name_gu || "",
      district: m.district_slug || "", role_en: "Executive Member", role_gu: "કાર્યકારી સભ્ય", role_key: "custom",
      photo_url: "", photo_key: "", note_en: "", note_gu: "", order: f.members.length } ] }));
    setShowAdd(false); setSearch(""); setResults([]);
  };
  const addExternal = () => setForm((f) => ({ ...f, members: [...f.members, {
    member_id: null, external: true, name_en: "", name_gu: "", district: "",
    role_en: "Executive Member", role_gu: "કાર્યકારી સભ્ય", role_key: "custom",
    photo_url: "", photo_key: "", note_en: "", note_gu: "", order: f.members.length }] }));
  const setM = (i, k, v) => setForm((f) => { const a = [...f.members]; a[i] = { ...a[i], [k]: v }; return { ...f, members: a }; });
  const moveM = (i, d) => setForm((f) => { const a = [...f.members]; const j = i + d; if (j < 0 || j >= a.length) return f; [a[i], a[j]] = [a[j], a[i]]; a.forEach((x, idx) => x.order = idx); return { ...f, members: a }; });
  const rmM = (i) => setForm((f) => ({ ...f, members: f.members.filter((_, x) => x !== i) }));
  const setRole = (i, val) => { const r = ROLES.find((x) => x[0] === val); if (r) { setM(i, "role_en", r[0]); setM(i, "role_gu", r[1]); } };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl font-semibold text-gmoaa-green">Committees</h2>
        <Button onClick={newCommittee} data-testid="committee-new" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover"><Plus size={16} className="mr-1" /> New committee</Button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-1.5">
          {committees.map((c) => (
            <div key={c.id} className={`rounded-lg border px-3 py-2 ${sel === c.id ? "border-gmoaa-green bg-gmoaa-green-light" : "border-gmoaa-border-subtle bg-white"}`}>
              <button onClick={() => open(c)} data-testid={`committee-item-${c.id}`} className="w-full text-left">
                <p className="text-sm font-medium text-gmoaa-text-dark">{c.name_en}</p>
                <p className="text-xs text-gmoaa-text-muted">{c.status === "current" ? "Current" : "Previous"} · {c.members?.length || 0} members</p>
              </button>
              <div className="mt-1 flex gap-1">
                <button onClick={() => exportPdf(c)} title="Export PDF" className="text-gmoaa-text-muted hover:text-gmoaa-green" data-testid={`committee-pdf-${c.id}`}><FileDown size={13} /></button>
                <button onClick={() => duplicate(c.id)} title="Duplicate" className="text-gmoaa-text-muted hover:text-gmoaa-green"><Copy size={13} /></button>
                {c.status === "current" && <button onClick={() => archive(c.id)} title="Archive" className="text-gmoaa-text-muted hover:text-gmoaa-saffron-hover"><Archive size={13} /></button>}
                <button onClick={() => del(c.id)} title="Delete" className="text-gmoaa-red"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-2">
          {form ? (
            <div className="space-y-4">
              <div className="space-y-3 rounded-xl border border-gmoaa-border-subtle bg-white p-5">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label="Committee name (English)" value={form.name_en} onChange={setMeta("name_en")} testid="committee-name-en" />
                  <Field label="Committee name (Gujarati)" value={form.name_gu} onChange={setMeta("name_gu")} />
                  <Field label="Term start" type="date" value={form.term_start} onChange={setMeta("term_start")} testid="committee-term-start" />
                  <Field label="Term end" type="date" value={form.term_end} onChange={setMeta("term_end")} />
                </div>
                <div className="flex flex-wrap items-center gap-5">
                  <div>
                    <p className="mb-1 text-xs font-medium text-gmoaa-text-muted">Status</p>
                    <Select value={form.status} onValueChange={setMeta("status")}>
                      <SelectTrigger className="h-9 w-40 border-gmoaa-border-subtle bg-white" data-testid="committee-status"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="current">Current</SelectItem><SelectItem value="past">Past</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <label className="flex items-center gap-2 text-sm"><Switch checked={form.show_public !== false} onCheckedChange={setMeta("show_public")} data-testid="committee-show" /> Show on public site</label>
                  <label className="flex items-center gap-2 text-sm"><Switch checked={!!form.confirmed} onCheckedChange={setMeta("confirmed")} data-testid="committee-confirm" /> Confirmed current</label>
                </div>
              </div>

              <div className="space-y-2 rounded-xl border border-gmoaa-border-subtle bg-white p-5">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gmoaa-text-dark">Members ({form.members.length})</p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setShowAdd((s) => !s)} data-testid="committee-addmember"><UserPlus size={14} className="mr-1" /> Add member</Button>
                    <Button size="sm" variant="outline" onClick={addExternal} data-testid="committee-addexternal">Add external</Button>
                  </div>
                </div>

                {showAdd && (
                  <div className="rounded-lg border border-gmoaa-border-subtle bg-gmoaa-surface p-3">
                    <div className="flex gap-2">
                      <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search roster by name, district or reg. no." className="h-8 text-sm" data-testid="committee-search" onKeyDown={(e) => e.key === "Enter" && doSearch()} />
                      <Button size="sm" onClick={doSearch} className="bg-gmoaa-green text-white"><Search size={14} /></Button>
                    </div>
                    <div className="mt-2 max-h-48 space-y-1 overflow-y-auto">
                      {results.map((m) => (
                        <button key={m.id} onClick={() => addMember(m)} data-testid={`committee-result-${m.id}`} className="flex w-full items-center justify-between rounded border border-gmoaa-border-subtle bg-white px-2 py-1.5 text-left text-xs hover:bg-gmoaa-green-light">
                          <span>{m.name_en} · {m.district_slug} · {m.member_id}</span><Plus size={13} />
                        </button>
                      ))}
                      {results.length === 0 && <p className="text-xs text-gmoaa-text-muted">Search to find registered members.</p>}
                    </div>
                  </div>
                )}

                {form.members.map((m, i) => (
                  <div key={i} className="rounded-lg border border-gmoaa-border-subtle p-3" data-testid={`committee-member-${i}`}>
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-xs font-medium text-gmoaa-text-dark">
                        #{i + 1} {m.member_id ? <span className="ml-1 rounded bg-gmoaa-green-light px-1.5 py-0.5 text-[10px] text-gmoaa-green">Roster</span> : <span className="ml-1 rounded bg-gmoaa-saffron-light px-1.5 py-0.5 text-[10px] text-gmoaa-saffron-hover">External</span>}
                      </span>
                      <div className="flex gap-1">
                        <button onClick={() => moveM(i, -1)} className="text-gmoaa-text-muted hover:text-gmoaa-green"><ArrowUp size={14} /></button>
                        <button onClick={() => moveM(i, 1)} className="text-gmoaa-text-muted hover:text-gmoaa-green"><ArrowDown size={14} /></button>
                        <button onClick={() => rmM(i)} className="text-gmoaa-red"><X size={15} /></button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <Input className="h-8 text-sm" value={m.name_en} onChange={(e) => setM(i, "name_en", e.target.value)} placeholder="Name (English)" disabled={!!m.member_id} />
                      <Input className="h-8 text-sm" value={m.name_gu} onChange={(e) => setM(i, "name_gu", e.target.value)} placeholder="Name (Gujarati)" disabled={!!m.member_id} />
                      <div>
                        <Select value={ROLES.some((r) => r[0] === m.role_en) ? m.role_en : "__custom"} onValueChange={(v) => v !== "__custom" && setRole(i, v)}>
                          <SelectTrigger className="h-8 border-gmoaa-border-subtle bg-white text-sm" data-testid={`committee-role-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ROLES.map((r) => <SelectItem key={r[0]} value={r[0]}>{r[0]}</SelectItem>)}
                            <SelectItem value="__custom">Custom…</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Input className="h-8 text-sm" value={m.role_en} onChange={(e) => setM(i, "role_en", e.target.value)} placeholder="Role (English)" />
                      <Input className="h-8 text-sm" value={m.role_gu} onChange={(e) => setM(i, "role_gu", e.target.value)} placeholder="Role (Gujarati)" />
                      <Input className="h-8 text-sm" value={m.note_en} onChange={(e) => setM(i, "note_en", e.target.value)} placeholder="Short note (English, optional)" />
                    </div>
                    <div className="mt-2">
                      <FileOrUrlField label={m.member_id ? "Photo override (default = member's profile photo)" : "Photo"} accept="image" value={m.photo_url} onChange={(v) => setM(i, "photo_url", v)} testid={`committee-photo-${i}`} />
                    </div>
                  </div>
                ))}
              </div>

              <Button onClick={save} data-testid="committee-save" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Save committee</Button>
              <Button variant="outline" onClick={() => exportPdf(committees.find((x) => x.id === sel) || form)} data-testid="committee-pdf-export" className="ml-2 border-gmoaa-green text-gmoaa-green hover:bg-gmoaa-green-light"><FileDown size={15} className="mr-1" /> Export PDF</Button>
            </div>
          ) : <p className="text-sm text-gmoaa-text-muted">Select a committee to edit, or create one. The current office bearers were migrated into a “Current Committee”.</p>}
        </div>
      </div>
    </div>
  );
};
